import React, { Component } from "react";
import PropTypes from "prop-types";
import ImageArrow from "components/utility/images/ImageArrow";
import ImageArrowUp from "components/utility/images/ImageArrowUp";
import { Scrollbars } from "react-custom-scrollbars-2";
import { sanitizeHtml } from "utils/sanitizeHtml";
import { areObjectsEqual } from "utils/objects";
import { isDescendant } from "utils/dom";

class Dropdown extends Component {
	static defaultProps = {
		itemHeight: 45,
		mobileAutoHeightMax: "calc(100vh - 13.75rem)",
		autoHeightMax: "12.65rem",
		embedded: false,
		initDropped: false,
		maxLength: 40,
		maxLengthMobile: 20,
		onOpen: () => {},
		onClose: () => {}
	};

	static propTypes = {
		itemHeight: PropTypes.number,
		mobileAutoHeightMax: PropTypes.string,
		autoHeightMax: PropTypes.string,
		name: PropTypes.string,
		label: PropTypes.string,
		items: PropTypes.arrayOf(PropTypes.object).isRequired,
		currentItem: PropTypes.object,
		className: PropTypes.string,
		showFilter: PropTypes.bool,
		scrollbarStyle: PropTypes.object,
		initDropped: PropTypes.bool,
		onOpen: PropTypes.func,
		onKeyArrows: PropTypes.func,
		onClose: PropTypes.func,
		smallMatchesMobileView: PropTypes.bool, // For use when mobile & tablet experience should be the same
		dataQASelect: PropTypes.string,
		dataQADropdownList: PropTypes.string
	};

	constructor(props) {
		super(props);
		this.dropdown = React.createRef();
		this.placeholder = React.createRef();
		this.list = React.createRef();
		this.scrollbar = React.createRef();
		this.focusEvent = false;
		this.filterTextTimeout = null;

		let currentIndex = null;
		if (props.currentItem && props.items && props.items.length) {
			props.items.forEach((item, key) => {
				if (item.key === props.currentItem.key) {
					currentIndex = key;
				}
			});
		}

		this.state = {
			isMobile: false,
			dropped: props.initDropped,
			currentItem: props.currentItem ? props.currentItem : null,
			items: props.items ? props.items : null,
			filterText: "",
			currentIndex: currentIndex,
			isTouchDevice: false,
			openedByFocus: false,
			accessibilitySelect: null,
			hoverCurrentItem: props.currentItem ? props.currentItem : null,
			isHover: false
		};
	}

	componentDidMount() {
		if (!this.props.label && !this.state.currentItem) {
			this.onSelected(this.state.items[0]);
		}
		document.addEventListener("mousedown", this.handleClickOutside);
		document.addEventListener("touchstart", this.onTouchStart);
		window.addEventListener("resize", this.onResize);
		this.onResize();
	}

	/**
	 *
	 * @param {*} event
	 * on mobile, the country select gets removed from the modal and appended to the body tag
	 * causing this function to run instead of the onClick of the country button that was selected
	 * checking the event target classes forces the onClick of the country button to run
	 */
	handleClickOutside = (event) => {
		const mobileDropdownClasses = [
			"dfa-dropdown-item",
			"dfa-dropdown-item-content",
			"dfa-dropdown-item-content-caption"
		];
		const elClicked = event.target.classList;
		if (this.state.dropped) {
			if (
				this.state.isMobile &&
				mobileDropdownClasses.some((elClass) => elClicked.contains(elClass))
			) {
				let itemCaption = "";
				if (event.target.classList.contains("dfa-dropdown-item")) {
					itemCaption = event.target.getAttribute("aria-label");
				}
				if (event.target.classList.contains("dfa-dropdown-item-content")) {
					itemCaption = event.target.parentNode.getAttribute("aria-label");
				}
				if (event.target.classList.contains("dfa-dropdown-item-content-caption")) {
					itemCaption = event.target.parentNode.parentNode.getAttribute("aria-label");
				}
				this.onSelected(...this.state.items.filter((item) => item.caption === itemCaption));
			} else if (this.state.isMobile && !isDescendant(this.list.current, event.target)) {
				this.handleDropdown();
			} else if (!this.state.isMobile && !isDescendant(this.dropdown.current, event.target)) {
				this.handleDropdown();
			}
		}
		if (
			!this.state.dropped &&
			this.state.isMobile &&
			event.target.classList.contains("ReactModal__Overlay")
		) {
			this.props.onClose(null, this.state.isMobile);
		}
	};

	onTouchStart = () => {
		if (!this.state.isTouchDevice) {
			this.setState({ isTouchDevice: true });
		}
	};

	componentDidUpdate(prevProps, prevState) {
		const newState = JSON.parse(JSON.stringify(this.state));
		let setState = false;
		if (prevState.dropped && !this.state.dropped) {
			newState.filterText = "";
			setState = true;
		} else if (
			prevProps.items !== this.props.items ||
			(prevProps.items && this.props.items && prevProps.items.length !== this.props.items.length)
		) {
			newState.filterText = "";
			setState = true;
		}

		if (
			!prevState.dropped &&
			this.state.dropped &&
			prevState.currentIndex >= 0 &&
			this.state.currentIndex === null
		) {
			const { scrollbar } = this.refs;
			scrollbar.scrollTop(0);
		}

		if (
			!areObjectsEqual(prevProps.currentItem, this.props.currentItem) &&
			this.state.items &&
			this.state.items.length
		) {
			let currentItem = null,
				currentIndex = null;
			this.state.items.forEach((item, key) => {
				if (this.props.currentItem && item.key === this.props.currentItem.key) {
					currentItem = this.props.currentItem;
					currentIndex = key;
				}
			});

			newState.currentItem = currentItem;
			newState.currentIndex = currentIndex;
			setState = true;
		}

		if (this.state.filterText.length - prevState.filterText.length > 2 && newState.items.length) {
			newState.currentItem = JSON.parse(JSON.stringify(newState.items[0]));
			newState.filterText = "";
			newState.dropped = false;
			setState = true;
			this.props.onSelected(newState.currentItem);
		}

		if (setState) {
			this.setState(newState);
		}
	}

	shouldComponentUpdate(nextProps, nextState) {
		if (
			nextProps.topLabel !== this.props.topLabel ||
			nextState.dropped !== this.state.dropped ||
			nextProps.error !== this.props.error ||
			nextState.filterText !== this.state.filterText ||
			!areObjectsEqual(nextProps.currentItem, this.props.currentItem) ||
			!areObjectsEqual(nextState.currentItem, this.state.currentItem) ||
			nextProps.items !== this.props.items ||
			(nextProps.items && this.props.items && nextProps.items.length !== this.props.items.length) ||
			nextState.currentIndex !== this.state.currentIndex
		) {
			return true;
		}
		return false;
	}

	static getDerivedStateFromProps(props, state) {
		if (
			props.items &&
			props.items.length &&
			state.items &&
			state.items.length &&
			props.items[0].key !== state.items[0].key
		) {
			return {
				items: props.items,
				currentItem: null,
				currentIndex: null
			};
		}
		return null;
	}

	componentWillUnmount() {
		window.removeEventListener("resize", this.onResize);
		document.removeEventListener("mousedown", this.handleClickOutside);
		document.removeEventListener("touchstart", this.onTouchStart);
		document.removeEventListener("keydown", this.onItemKeyDown);
	}

	onResize = () => {
		const { embedded, initDropped, smallMatchesMobileView } = this.props;
		const mobileViewBreakpoint = smallMatchesMobileView ? 1023 : 767;
		const isMobile = window.innerWidth <= mobileViewBreakpoint;
		const dropdownLists = document.querySelectorAll(".dfa-dropdown");

		dropdownLists.forEach((dropdown) => {
			dropdown.popover = isMobile ? "manual" : null;
			if (dropdown.popover) {
				dropdown.togglePopover(false);
			}
		});
		if (embedded) {
			this.setState({
				isMobile,
				dropped: isMobile && initDropped
			});
		} else {
			this.setState({ dropped: false });
			if (isMobile && this.list.current) {
				this.setState({ isMobile: true });
			} else {
				if (this.placeholder && this.placeholder.current) {
					this.placeholder.current.insertBefore(this.list.current, null);

					this.setState({ isMobile: false });
				}
			}
		}
	};

	handleDropdown = (byFocus, item = null) => {
		const { dropped, droppedByFocus } = this.state;
		const { name, onOpen, onClose } = this.props;
		const currentItem = item ? item : this.state.currentItem ? this.state.currentItem : null;
		const newState = {};
		const focusables = document.querySelectorAll(
			`select:not(#dfa-dropdown-filter-input-${name}), input, button:not(.dfa-dropdown-item), a`
		);
		if (dropped) {
			if (
				(currentItem && !this.state.currentItem) ||
				(currentItem && this.state.currentItem && currentItem.key !== this.state.currentItem.key)
			) {
				newState.currentItem = JSON.parse(JSON.stringify(currentItem));
			}
			onClose(currentItem);
			if (droppedByFocus) {
				newState.droppedByFocus = false;
			}
			document.removeEventListener("keydown", this.onItemKeyDown);
			focusables.forEach((focusable) => {
				focusable.removeEventListener("focus", this.focusOnOther);
			});
			this.clearFilterTextTimeout();
		} else {
			const anyOtherDropdown = document.querySelectorAll(
				`.dfa-dropdown-container:not(#dfa-dropdown-${this.props.name})`
			);
			anyOtherDropdown.forEach((otherDropdown) => {
				otherDropdown.addEventListener("click", this.handleClickOutside);
			});
			onOpen();
			if (byFocus) {
				newState.droppedByFocus = true;
			}
			document.addEventListener("keydown", this.onItemKeyDown);
			focusables.forEach((focusable) => {
				focusable.addEventListener("focus", this.focusOnOther);
			});
			if (this.state.isMobile) {
				this.list.current.showPopover();
			}
		}
		newState.dropped = !dropped;
		this.setState(newState);
	};

	focusOnOther = () => {
		if (this.state.dropped) {
			this.handleDropdown();
		}
	};

	onSelected = (item) => {
		if (this.state.currentItem && this.state.currentItem.key === item.key) {
			this.handleDropdown();
		} else {
			this.handleDropdown(false, item);
		}
	};

	onFilterChange = (filterText) => {
		let newState = { filterText: filterText };
		let found = null,
			index = null,
			scrollTop = 0;
		const { scrollbar } = this.refs;
		const list = this.list.current;
		const regex = new RegExp(`^${filterText}`, "i");
		const domItems = list.querySelectorAll(".dfa-dropdown-item");

		if (filterText) {
			found = this.props.items.find((item, key) => {
				index = key;
				return (item.caption && regex.test(item.caption)) || (item.extra && regex.test(item.extra));
			});

			if (found && index !== null) {
				for (let i = 1; i < domItems.length; i++) {
					if (i <= index) {
						scrollTop += domItems[i].offsetHeight;
					}
				}
				scrollbar.scrollTop(scrollTop);
				newState.currentItem = found;
				newState.currentIndex = index;
			}
		}

		this.setState(newState);
	};

	onAccessibilitySelectChange = (e) => {
		const { items } = this.state;
		const key = e.target.value;
		let currentItem = null,
			currentIndex = null;
		if (key && key !== this.state.accessibilitySelect) {
			currentItem = items.filter((item, index) => {
				if (item.key === key) {
					currentIndex = index;
					return true;
				}
				return false;
			});
			if (currentItem.length) {
				currentItem = JSON.parse(JSON.stringify(currentItem[0]));
				if (typeof this.props.onClose === "function") {
					this.props.onClose(currentItem);
				}
				this.setState({
					currentItem: currentItem,
					droppedByFocus: false,
					currentIndex: currentIndex,
					dropped: false,
					accessibilitySelect: key
				});
				this.clearFilterTextTimeout();
			} else {
				if (typeof this.props.onClose === "function") {
					this.props.onClose(null);
				}
				this.setState({
					currentItem: null,
					droppedByFocus: false,
					currentIndex: null,
					dropped: false,
					accessibilitySelect: null
				});
				this.clearFilterTextTimeout();
			}
		}
	};

	handleLabelFocus = () => {
		if (!this.state.dropped && !this.droppedByFocus) {
			this.handleDropdown();
		}
	};

	handleLabelMousedown = (e) => {
		e.nativeEvent.stopImmediatePropagation();
		this.handleDropdown();
		this.setState({
			hoverCurrentItem: this.state.currentItem,
			isHover: false
		});
	};

	stopPropagation = (e) => {
		e.stopPropagation();
		if (e.stopImmediatePropagation) {
			e.stopImmediatePropagation();
		} else if (e.nativeEvent.stopImmediatePropagation) {
			e.nativeEvent.stopImmediatePropagation();
		}
	};

	onItemKeyDown = (event) => {
		const { scrollbar } = this.refs;
		const { name } = this.props;
		const { isMobile, isTouchDevice, dropped, currentItem, currentIndex, filterText, items } =
			this.state;
		const list = this.list.current,
			regexp = new RegExp("^(key)?[a-z]{1}$", "i");
		let code = event.code ? event.code.toLowerCase() : event.key ? event.key.toLowerCase() : "";
		if (list && !isMobile && !isTouchDevice) {
			const domItems = list.querySelectorAll(".dfa-dropdown-item");
			if (dropped && items && items.length) {
				if (code === "arrowup" || code === "arrowdown") {
					const focusables = document.querySelectorAll(
						`select:not(#dfa-dropdown-filter-input-${name}), input, button:not(.dfa-dropdown-item), a`
					);
					let nextIndex = 0,
						scrollTop = 0;
					this.stopPropagation(event);
					event.preventDefault();
					focusables.forEach((focusable) => {
						focusable.addEventListener("focus", this.focusOnOther);
					});
					if (currentItem === null || currentIndex === null) {
						scrollbar.scrollTop(0);
						this.setState({ currentItem: items[0], currentIndex: 0 });
						if (typeof this.props.onKeyArrows === "function") {
							this.props.onKeyArrows(items[0]);
						}
					} else {
						if (code === "arrowup") {
							if (currentIndex > 0) {
								nextIndex = currentIndex - 1;
							} else if (currentIndex === 0) {
								nextIndex = items.length - 1;
							}
						} else if (code === "arrowdown") {
							if (items.length > currentIndex + 1) {
								nextIndex = currentIndex + 1;
							} else if (items.length === currentIndex + 1) {
								nextIndex = 0;
							}
						}
						for (let i = 1; i < domItems.length; i++) {
							if (i <= nextIndex) {
								scrollTop += domItems[i].offsetHeight;
							}
						}
						scrollbar.scrollTop(scrollTop);
						this.setState({ currentItem: items[nextIndex], currentIndex: nextIndex });
						if (typeof this.props.onKeyArrows === "function") {
							this.props.onKeyArrows(items[nextIndex]);
						}
					}
				} else if (code === "esc" || code === "escape" || code === "enter") {
					this.handleDropdown();
				} else if (code === "backspace") {
					if (filterText.length > 0) {
						this.clearFilterTextTimeout();
						this.filterTextStartTimeout();
						this.onFilterChange(filterText.substring(0, filterText.length - 1));
					} else {
						this.clearFilterTextTimeout();
					}
				} else if (regexp.test(code)) {
					this.clearFilterTextTimeout();
					this.filterTextStartTimeout();
					const letter = code.replace("key", "");
					this.onFilterChange(`${filterText}${letter}`);
				}
			} else if (["arrowup", "arrowdown"].includes(code)) {
				this.stopPropagation(event);
				event.preventDefault();
				if (!dropped) {
					this.handleDropdown();
				}
			}
		}
	};

	filterTextStartTimeout = (time = 1.5) => {
		this.filterTextTimeout = setTimeout(() => {
			this.onFilterChange("");
		}, time * 1000); // in miliseconds
	};

	clearFilterTextTimeout = () => {
		if (this.filterTextTimeout) {
			clearTimeout(this.filterTextTimeout);
		}
	};

	onHoverEnter = (item) => {
		this.setState({ hoverCurrentItem: item, isHover: true });
		this.forceUpdate();
	};

	onHoverLeave = () => {
		this.setState({ hoverCurrentItem: this.state.currentItem, isHover: false });
		this.forceUpdate();
	};

	render() {
		const { dropped, currentItem, isMobile, items, hoverCurrentItem, isHover } = this.state;
		const {
			name,
			label,
			className,
			error,
			maxLengthMobile,
			maxLength,
			mobileAutoHeightMax,
			autoHeightMax,
			scrollbarStyle,
			embedded,
			containerClassName = "",
			errorLabel = "",
			dataQASelect = null,
			dataQADropdownList = null
		} = this.props;

		let currentCaption = null,
			select = null;
		let { topLabel } = this.props;

		if (currentItem) {
			const maxLen = isMobile ? maxLengthMobile : maxLength;
			currentCaption = sanitizeHtml(currentItem.caption);
			currentCaption =
				currentCaption.length > maxLen
					? currentCaption.substring(0, maxLen).trimEnd() + "..."
					: currentCaption;
		}
		if (topLabel) {
			topLabel = (
				<div
					className={
						"dfa-dropdown-top-label dfa-dropdown-top-label-" +
						dropped +
						(error ? " dfa-dropdown-top-label-error" : "")
					}
					dangerouslySetInnerHTML={{ __html: sanitizeHtml(error ? errorLabel : topLabel) }}
				/>
			);
		}
		const style = {};
		if (dropped) {
			style.zIndex = 100;
		}
		select = (
			<div className="dfa-dropdown-filter" key="filter">
				<select
					id={`dfa-dropdown-filter-input-${name}`}
					name="country"
					style={{ visibility: "hidden" }}
					tabIndex="-1"
					className="dfa-dropdown-filter"
					onBlur={this.onAccessibilitySelectChange}
					onChange={this.onAccessibilitySelectChange}
				>
					<option key="none" value="">
						None
					</option>
					{items.map((item) => (
						<option key={item.key} value={item.key}>
							{item.caption}
						</option>
					))}
				</select>
			</div>
		);

		return (
			<div
				id={`dfa-dropdown-${name}`}
				className={`dfa-dropdown-container ${containerClassName} ${
					embedded ? "dfa-dropdown-embedded" : ""
				} ${embedded && dropped ? "dfa-dropdown-embedded-true" : ""}`}
				style={style}
				ref={this.dropdown}
				data-testid="dfa-dropdown"
			>
				{select}
				{topLabel}
				<div
					className={
						"dfa-dropdown-init dfa-dropdown-init-" +
						(dropped ? "true" : "false") +
						(error ? " dfa-dropdown-error" : "")
					}
					onMouseDown={this.handleLabelMousedown}
					onFocus={this.handleLabelFocus}
					role="button"
					tabIndex="0"
					aria-label="dropdown menu"
					aria-expanded={dropped ? true : false}
					data-qa={dataQASelect}
				>
					<span
						className="dfa-dropdown-init-label"
						dangerouslySetInnerHTML={{
							__html: currentCaption ? currentCaption : sanitizeHtml(label)
						}}
					/>
					{dropped ? <ImageArrowUp /> : <ImageArrow />}
				</div>
				<div className="dfa-dropdown-placeholder" ref={this.placeholder}></div>

				<div
					className={"dfa-dropdown dfa-dropdown-" + dropped + ` ${className ? className : ""}`}
					ref={this.list}
					data-qa={dataQADropdownList}
				>
					<div className={"dfa-dropdown-mobile-title-" + (dropped || isMobile)}>
						<span
							className="label-3-left-medium-cool-gray"
							dangerouslySetInnerHTML={{ __html: sanitizeHtml(label) }}
						/>
					</div>
					<Scrollbars
						ref="scrollbar"
						className="dfa-dropdown-list"
						autoHeight
						autoHeightMin="1rem"
						autoHeightMax={isMobile ? mobileAutoHeightMax : autoHeightMax}
						renderThumbVertical={(props) => (
							<div {...props} className="dfa-dropdown-thumb-vertical" />
						)}
						style={scrollbarStyle}
					>
						{items.map((item) => {
							return (
								<button
									className={
										"dfa-dropdown-item " +
										((currentItem && item.key === currentItem.key && !isHover) ||
										(hoverCurrentItem !== null && hoverCurrentItem.key === item.key && isHover)
											? "dfa-dropdown-item-selected dfa-dropdown-item-focused"
											: "")
									}
									key={item.key}
									onClick={() => {
										this.onSelected(item);
									}}
									onMouseEnter={() => this.onHoverEnter(item)}
									onMouseLeave={() => this.onHoverLeave()}
									aria-label={item.caption}
									tabIndex="-1"
									type="button"
									data-qa={item.caption}
								>
									<span className="dfa-dropdown-item-content">
										<span
											className="dfa-dropdown-item-content-prefix"
											dangerouslySetInnerHTML={{
												__html: sanitizeHtml(item.prefix ? item.prefix : "")
											}}
										/>
										<span
											className="dfa-dropdown-item-content-caption"
											dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.caption) }}
										/>
										<span
											className="dfa-dropdown-item-content-extra"
											dangerouslySetInnerHTML={{
												__html: sanitizeHtml(item.extra ? item.extra : "")
											}}
										/>
									</span>
								</button>
							);
						})}
					</Scrollbars>
				</div>
			</div>
		);
	}
}

export default Dropdown;
